/* Changing cash from one currency into another.
 *
 * A broker holding two currency accounts — XTB's złoty side and its dollar side
 * — settles a conversion between them at whatever rate it felt like giving you
 * that morning, which is not the National Bank's rate and not the one on any
 * screen in this app. The book had no way to record that at all: cash balances
 * could be typed over, so the only way to move money between them was to edit
 * two numbers and leave no trace of why either changed.
 *
 * The one thing this must not become is a deposit.
 *
 * Converting 1,000 złoty into 250 dollars adds nothing to the book and takes
 * nothing out of it — the same money is in a different currency. Money paid in
 * (§5.1 `contributed`) is what separates what the market added from what you
 * funded, and putting a conversion through it would report a quarter of a
 * thousand dollars of new capital and quietly destroy every return figure that
 * hangs off it. So conversions are their own record and touch `cashflows`
 * nowhere.
 *
 * What it does move is the book's total, slightly and honestly: a conversion
 * done at a worse rate than the one the book values at leaves fewer dollars
 * than it started with, and that difference is real. */

import type { CashBalance, CashConversion, Currency } from '../types';

export interface ConversionOutcome {
  balances: CashBalance[];
  /** What the broker's rate worked out at: units of `to` per unit of `from`. */
  rate: number;
  /** True when the account being drawn on does not hold enough. Not refused —
      a balance can be out of date, and correcting it is the user's business —
      but the screen says so before it is saved. */
  overdrawn: boolean;
  /** The balance the money is being taken from, after the conversion. */
  fromAfter: number;
  toAfter: number;
}

/** Null when the conversion does not describe one: a figure at or below zero,
    or the same currency on both sides — which is a transfer, not an exchange,
    and this book has no concept of one. */
/** When the money has already left the account it came from.
 *
 * A withdrawal off an exchange is read back by the account sync, which writes
 * the balance the exchange actually holds — so by the time it is being recorded
 * here the debit has happened. Taking it off again would charge the account
 * twice, which is the same double entry `closePerp` guards against with
 * `walletAlreadySettled`, and for the same reason: a record of something that
 * has already happened is written down, not re-run. */
export interface ConvertOptions {
  sourceAlreadySettled?: boolean;
}

export function convertCash(
  balances: CashBalance[],
  c: Pick<CashConversion, 'fromAccount' | 'fromAmount' | 'fromCurrency' | 'toAccount' | 'toAmount' | 'toCurrency'>,
  opts: ConvertOptions = {},
): ConversionOutcome | null {
  if (!(c.fromAmount > 0) || !(c.toAmount > 0)) return null;
  if (c.fromAccount === c.toAccount && c.fromCurrency === c.toCurrency) return null;

  const next = balances.map((b) => ({ ...b }));
  const find = (account: string) => next.find((b) => b.account === account) ?? null;

  const from = find(c.fromAccount);
  const to = find(c.toAccount);

  /* Drawing on a balance held in another currency is not something this can
     work out — the amount given is in `fromCurrency`, and subtracting it from a
     balance denominated in something else would be arithmetic on two different
     units. */
  if (from && from.currency !== c.fromCurrency) return null;

  const fromBefore = from?.amount ?? 0;
  const fromAfter = opts.sourceAlreadySettled ? fromBefore : fromBefore - c.fromAmount;

  if (opts.sourceAlreadySettled) {
    /* Nothing to take off: the balance on record is already the one after the
       money left. */
  } else if (from) {
    from.amount = fromAfter;
  } else {
    next.push({ account: c.fromAccount, amount: fromAfter, currency: c.fromCurrency as Currency });
  }

  let toAfter: number;
  if (to && to.currency === c.toCurrency) {
    toAfter = to.amount + c.toAmount;
    to.amount = toAfter;
  } else if (to) {
    /* The account held a different currency. One balance per account, so this
       is the account changing what it is denominated in — which is exactly what
       converting the whole of it means. Anything left in the old currency would
       have nowhere to sit, so the amount given must be the whole balance; the
       caller is shown the overdraft if it is not. */
    toAfter = c.toAmount;
    to.amount = toAfter;
    to.currency = c.toCurrency as Currency;
  } else {
    toAfter = c.toAmount;
    next.push({ account: c.toAccount, amount: toAfter, currency: c.toCurrency as Currency });
  }

  return {
    balances: next,
    rate: c.toAmount / c.fromAmount,
    /* A balance already settled elsewhere cannot be overdrawn by this record:
       it is the figure the exchange itself reported afterwards. */
    overdrawn: !opts.sourceAlreadySettled && fromAfter < 0,
    fromAfter,
    toAfter,
  };
}

/** "1 PLN = 0.2694 USD", the way a broker's confirmation writes it. */
export function rateSentence(c: Pick<CashConversion, 'fromAmount' | 'fromCurrency' | 'toAmount' | 'toCurrency'>): string {
  if (!(c.fromAmount > 0)) return '';
  const rate = c.toAmount / c.fromAmount;
  const decimals = rate < 0.01 ? 6 : rate < 1 ? 4 : 4;
  return `1 ${c.fromCurrency} = ${rate.toFixed(decimals)} ${c.toCurrency}`;
}

/** How the rate you were given compares with the book's own, as a percentage.
 *
 * Positive means you did better than the reference. Null when there is no
 * reference to compare with — never zero, which would read as "exactly the
 * market rate" and is a different claim from "not known". */
export function rateAgainst(
  c: Pick<CashConversion, 'fromAmount' | 'toAmount'>,
  referenceRate: number | null,
): number | null {
  if (referenceRate === null || !(referenceRate > 0) || !(c.fromAmount > 0)) return null;
  const got = c.toAmount / c.fromAmount;
  return ((got - referenceRate) / referenceRate) * 100;
}
